import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { WrapperGoogleMaps } from "./WrapperGoogleMaps";

/**
 * Custom Google Autocomplete Input with dark theme styling
 */
const CustomAutocompleteInput = (props) => {
  const {
    googleReady,
    onChangeAddress,
    types,
    fields,
    countryCode,
    placeholder,
    className,
    value,
    onChange,
  } = props;

  const inputRef = useRef();
  const autocompleteRef = useRef();
  const requestIdRef = useRef(0);
  const composingRef = useRef(false);
  const compositionCommitRef = useRef(null);
  const latestInputRef = useRef(value || "");
  const blurTimerRef = useRef(null);
  const listboxId = useId();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value || "");

  const options = {
    types,
    fields,
  };

  if (countryCode !== "*") {
    options.componentRestrictions = {
      country: countryCode,
    };
  }

  /**
   * Maps prediction data from different API formats to a unified structure.
   * Handles three possible input formats:
   * 1. Legacy API: { place_id, structured_formatting: { main_text, secondary_text } }
   * 2. New API (full suggestion): { placePrediction: { placeId, structuredFormat: { mainText, secondaryText } } }
   * 3. New API (extracted placePrediction): { placeId, structuredFormat: { mainText, secondaryText } }
   */
  const mapSuggestionPrediction = (prediction) => {
    // Extract place ID (handle all formats)
    const placeId =
      prediction?.place_id || // Legacy API
      prediction?.placePrediction?.placeId || // New API (full suggestion)
      prediction?.placeId; // New API (extracted placePrediction)

    // Extract main text (handle all formats)
    const mainText =
      prediction?.structured_formatting?.main_text || // Legacy API
      prediction?.placePrediction?.structuredFormat?.mainText?.text || // New API (full)
      prediction?.structuredFormat?.mainText?.text || // New API (extracted)
      prediction?.placePrediction?.text?.text || // New API fallback (full)
      prediction?.text?.text; // New API fallback (extracted)

    // Extract secondary text (handle all formats)
    const secondaryText =
      prediction?.structured_formatting?.secondary_text || // Legacy API
      prediction?.placePrediction?.structuredFormat?.secondaryText?.text || // New API (full)
      prediction?.structuredFormat?.secondaryText?.text || // New API (extracted)
      "";

    // Extract description (handle all formats)
    const description =
      prediction?.description || // Legacy API
      prediction?.placePrediction?.text?.text || // New API (full)
      prediction?.text?.text || // New API (extracted)
      mainText ||
      "";

    return {
      ...prediction,
      place_id: placeId,
      description: description,
      structured_formatting: {
        main_text: mainText || "",
        secondary_text: secondaryText,
      },
    };
  };

  /**
   * Initialize the legacy AutocompleteService as fallback
   */
  const initLegacyAutocomplete = () => {
    if (window.google?.maps?.places?.AutocompleteService) {
      const service = new window.google.maps.places.AutocompleteService();
      autocompleteRef.current = {
        type: "legacyAutocompleteService",
        getPlacePredictions: (query) =>
          new Promise((resolve) => {
            service.getPlacePredictions(
              {
                input: query,
                ...options,
              },
              (predictions, status) => {
                resolve({
                  predictions: predictions?.map(mapSuggestionPrediction) || [],
                  status,
                });
              },
            );
          }),
      };
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!googleReady || !onChangeAddress) return;

    const fetchAutocompleteSuggestions =
      window.google?.maps?.places?.AutocompleteSuggestion
        ?.fetchAutocompleteSuggestions;

    if (typeof fetchAutocompleteSuggestions === "function") {
      autocompleteRef.current = {
        type: "autocompleteSuggestion",
        getPlacePredictions: async (query) => {
          try {
            const suggestionRequest = {
              input: query,
            };

            if (countryCode && countryCode !== "*") {
              suggestionRequest.includedRegionCodes = [
                countryCode.toLowerCase(),
              ];
            }

            const response =
              await fetchAutocompleteSuggestions(suggestionRequest);

            // Pass the full suggestion object to the mapper (not just placePrediction)
            const predictions =
              response?.suggestions
                ?.map((suggestion) => mapSuggestionPrediction(suggestion))
                ?.filter((prediction) => Boolean(prediction?.place_id)) || [];

            return {
              predictions,
              status: window.google.maps.places.PlacesServiceStatus.OK,
            };
          } catch (error) {
            console.warn(
              "New AutocompleteSuggestion API failed, falling back to legacy.",
            );
            // Re-throw to trigger fallback in caller
            throw error;
          }
        },
      };
      return;
    }

    // Initialize legacy API if new API not available
    initLegacyAutocomplete();
  }, [countryCode, googleReady, onChangeAddress]);

  useEffect(() => {
    const next = value || "";
    if (next === latestInputRef.current) return;
    latestInputRef.current = next;
    compositionCommitRef.current = null;
    requestIdRef.current += 1;
    setInputValue(next);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  }, [value]);

  useEffect(() => {
    compositionCommitRef.current = null;
    requestIdRef.current += 1;
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    return () => {
      compositionCommitRef.current = null;
      requestIdRef.current += 1;
      clearTimeout(blurTimerRef.current);
    };
  }, [countryCode, googleReady]);

  /**
   * Try legacy API as fallback when new API fails
   */
  const tryLegacyFallback = (query, currentRequestId) => {
    // Initialize legacy service if not already
    if (autocompleteRef.current?.type !== "legacyAutocompleteService") {
      if (!initLegacyAutocomplete()) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
    }

    // Retry with legacy service
    autocompleteRef.current
      .getPlacePredictions(query)
      .then(({ predictions, status }) => {
        if (currentRequestId !== requestIdRef.current) return;

        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions?.length
        ) {
          setSuggestions(predictions);
          setShowSuggestions(true);
          setSelectedIndex(-1);
          return;
        }

        setSuggestions([]);
        setShowSuggestions(false);
      })
      .catch(() => {
        if (currentRequestId !== requestIdRef.current) return;
        setSuggestions([]);
        setShowSuggestions(false);
      });
  };

  const requestSuggestions = (query) => {
    const currentRequestId = ++requestIdRef.current;
    setSelectedIndex(-1);
    setSuggestions([]);
    setShowSuggestions(false);
    if (!composingRef.current && query.length > 2 && autocompleteRef.current) {
      autocompleteRef.current
        .getPlacePredictions(query)
        .then(({ predictions, status }) => {
          if (currentRequestId !== requestIdRef.current) return;

          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions?.length
          ) {
            setSuggestions(predictions);
            setShowSuggestions(true);
            setSelectedIndex(-1);
            return;
          }

          setSuggestions([]);
          setShowSuggestions(false);
        })
        .catch(() => {
          if (currentRequestId !== requestIdRef.current) return;

          // If using new API and it failed, try to fallback to legacy
          if (autocompleteRef.current?.type === "autocompleteSuggestion") {
            console.warn("New API failed, attempting legacy fallback.");
            tryLegacyFallback(query, currentRequestId);
            return;
          }

          setSuggestions([]);
          setShowSuggestions(false);
        });
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e) => {
    const query = e.target.value;
    latestInputRef.current = query;
    setInputValue(query);
    onChange && onChange(e);
    if (compositionCommitRef.current === query) {
      compositionCommitRef.current = null;
      return;
    }
    compositionCommitRef.current = null;
    requestSuggestions(query);
  };

  const handleSuggestionClick = (prediction) => {
    const currentRequestId = ++requestIdRef.current;
    if (window.google?.maps?.places?.PlacesService) {
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );

      service.getDetails(
        {
          placeId: prediction.place_id,
          fields:
            fields && fields.length > 0
              ? fields
              : [
                  "place_id",
                  "name",
                  "formatted_address",
                  "geometry",
                  "address_components",
                  "international_phone_number",
                  "opening_hours",
                  "photos",
                  "price_level",
                  "rating",
                  "user_ratings_total",
                  "website",
                  "url",
                ],
        },
        (place, status) => {
          if (currentRequestId !== requestIdRef.current) return;
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            const addressObj = {
              street_number: null,
              neighborhood: null,
              route: null,
              city_name: null,
              locality: null,
              sublocality: null,
              country: null,
              country_code: null,
              state: null,
              state_code: null,
              zipcode: null,
            };

            const cityFallback = place?.address_components?.find((component) =>
              component.types.includes("administrative_area_level_2"),
            );

            if (place?.address_components) {
              for (const component of place.address_components) {
                const addressType = component.types[0];
                if (addressType === "postal_code") {
                  addressObj.zipcode = component.short_name;
                }
                if (addressType === "street_number") {
                  addressObj.street_number = component.long_name;
                }
                if (addressType === "neighborhood") {
                  addressObj.neighborhood = component.long_name;
                }
                if (addressType === "route") {
                  addressObj.route = component.long_name;
                }
                if (addressType === "locality") {
                  addressObj.city_name =
                    component.long_name || cityFallback?.long_name;
                  addressObj.locality = component.long_name;
                }
                if (component.types?.includes("sublocality")) {
                  addressObj.sublocality = component.long_name;
                }
                if (addressType === "country") {
                  addressObj.country = component.long_name;
                  addressObj.country_code = component.short_name;
                }
                if (addressType === "administrative_area_level_1") {
                  addressObj.state = component.long_name;
                  addressObj.state_code = component.short_name;
                }
              }
            }

            const address = {
              address: place.formatted_address,
              name: place.name,
              location: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              },
              utc_offset: place.utc_offset_minutes,
              map_data: {
                library: "google",
                place_id: place.place_id,
              },
              phone: place.international_phone_number,
              price_level: place.price_level,
              rating: place.rating,
              user_ratings_total: place.user_ratings_total,
              website: place.website,
              url: place.url,
              photos: place.photos
                ? place.photos.map((photo) => ({
                    height: photo.height,
                    width: photo.width,
                    photo_reference: photo.photo_reference,
                    getUrl: () =>
                      photo.getUrl({ maxWidth: 400, maxHeight: 400 }),
                  }))
                : [],
              opening_hours: place.opening_hours,
              ...addressObj,
            };

            onChangeAddress(address);
            latestInputRef.current = place.formatted_address;
            setInputValue(place.formatted_address);
            setShowSuggestions(false);
          }
        },
      );
    }
  };

  const handleKeyDown = (e) => {
    if (
      composingRef.current ||
      e.nativeEvent.isComposing ||
      e.keyCode === 229
    ) {
      if (e.key === "Enter") e.preventDefault();
      return;
    }
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow clicks
    clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onCompositionStart={() => {
          composingRef.current = true;
          compositionCommitRef.current = null;
          requestIdRef.current += 1;
          setSuggestions([]);
          setShowSuggestions(false);
          setSelectedIndex(-1);
        }}
        onCompositionEnd={(e) => {
          composingRef.current = false;
          const query = e.currentTarget.value;
          latestInputRef.current = query;
          setInputValue(query);
          compositionCommitRef.current = query;
          requestSuggestions(query);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => setShowSuggestions(suggestions.length > 0)}
        placeholder={placeholder}
        disabled={!googleReady}
        className={className}
        autoComplete="new-password"
        role="combobox"
        aria-label={props["aria-label"] || placeholder}
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={showSuggestions ? listboxId : undefined}
        aria-activedescendant={
          selectedIndex >= 0
            ? `${listboxId}-option-${selectedIndex}`
            : undefined
        }
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          id={listboxId}
          role="listbox"
          className="border-border bg-popover text-popover-foreground absolute z-[var(--layer-dropdown)] mt-1 max-h-60 w-full overflow-y-auto rounded-md border shadow-[var(--od-shadow-2)]"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`cursor-pointer border-b border-gray-700 px-4 py-3 transition-colors last:border-b-0 ${
                index === selectedIndex
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:bg-gray-700"
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className="mt-1 flex-shrink-0">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`truncate text-sm font-medium ${
                      index === selectedIndex ? "text-white" : "text-white"
                    }`}
                  >
                    {suggestion.structured_formatting?.main_text ||
                      suggestion.description}
                  </p>
                  <p
                    className={`truncate text-xs ${
                      index === selectedIndex
                        ? "text-blue-100"
                        : "text-gray-400"
                    }`}
                  >
                    {suggestion.structured_formatting?.secondary_text || ""}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <div
            className="ordering-catalog-provider-identity border-t border-gray-700 px-4 py-2"
            data-provider-identity="google"
          >
            <div className="flex items-center justify-end space-x-1 text-xs text-gray-500">
              <span>Powered by</span>
              <span className="font-semibold text-blue-400">Google</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CustomAutocompleteInput.propTypes = {
  googleReady: PropTypes.bool,
  onChangeAddress: PropTypes.func,
  types: PropTypes.arrayOf(PropTypes.string),
  fields: PropTypes.arrayOf(PropTypes.string),
  countryCode: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export const CustomGoogleAutocompleteInput = WrapperGoogleMaps(
  CustomAutocompleteInput,
);
